import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faShieldAlt,
  faBolt,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

const Welcome = () => {
  return (
    <div className="welcome-container">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-icon">🚀</span>
            <span>Professional Trading Platform</span>
          </div>
          
          <h1 className="hero-title">
            Welcome to <span className="text-gradient">TradeJet</span>
          </h1>
          
          <p className="hero-subtitle">
            Advanced algorithmic trading platform for smart investors. 
            Monitor your trades, analyze performance, and make data-driven decisions.
          </p>
          
          <div className="hero-buttons">
            <Link to="/trade_app/login" className="btn-primary-custom">
              <FontAwesomeIcon icon={faArrowRight} className="btn-icon" />
              Get Started
            </Link>
            <Link to="/trade_app/register" className="btn-secondary-custom">
              Create Account
            </Link>
          </div>
        </div>

        {/* Stats Section */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
            <div className="stat-value">Real-time</div>
            <div className="stat-label">Market Analysis</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faShieldAlt} />
            </div>
            <div className="stat-value">Secure</div>
            <div className="stat-label">Data Protection</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faBolt} />
            </div>
            <div className="stat-value">Fast</div>
            <div className="stat-label">Execution Speed</div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <h2 className="section-title">Platform Features</h2>
        
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3>Trade Monitoring</h3>
            <p>Track all your trades in real-time with detailed analytics and performance metrics.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">📈</div>
            <h3>Advanced Charts</h3>
            <p>Interactive charts with technical indicators for comprehensive market analysis.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">📋</div>
            <h3>Daily Reports</h3>
            <p>Automated daily trade reports with P&L summaries and performance insights.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">🔒</div>
            <h3>Role-Based Access</h3>
            <p>Secure access control with admin and user roles for team management.</p>
          </div>
        </div>
      </div>

      <style>{`
        .welcome-container {
          padding: 20px 0;
          min-height: calc(100vh - 120px);
        }

        .hero-section {
          text-align: center;
          padding: 60px 20px 80px;
          position: relative;
        }

        .hero-content {
          max-width: 700px;
          margin: 0 auto;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(6, 182, 212, 0.1);
          border: 1px solid rgba(6, 182, 212, 0.3);
          border-radius: 50px;
          padding: 8px 20px;
          margin-bottom: 28px;
          font-size: 14px;
          color: #06b6d4;
        }

        .badge-icon {
          font-size: 16px;
        }

        .hero-title {
          font-size: 48px;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 20px;
          line-height: 1.2;
        }

        .text-gradient {
          background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 18px;
          color: #94a3b8;
          line-height: 1.7;
          margin-bottom: 36px;
        }

        .hero-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn-primary-custom {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
          color: white;
          padding: 14px 32px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 16px;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px -5px rgba(6, 182, 212, 0.4);
        }

        .btn-primary-custom:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 40px -5px rgba(6, 182, 212, 0.5);
          color: white;
          text-decoration: none;
        }

        .btn-secondary-custom {
          display: inline-flex;
          align-items: center;
          background: transparent;
          border: 1px solid #334155;
          color: #94a3b8;
          padding: 14px 32px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 16px;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .btn-secondary-custom:hover {
          border-color: #06b6d4;
          color: #06b6d4;
          text-decoration: none;
        }

        .btn-icon {
          font-size: 14px;
        }

        /* Stats Grid */
        .stats-grid {
          display: flex;
          justify-content: center;
          gap: 24px;
          margin-top: 60px;
          flex-wrap: wrap;
        }

        .stat-card {
          background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
          border: 1px solid #334155;
          border-radius: 16px;
          padding: 28px 36px;
          text-align: center;
          min-width: 180px;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          border-color: rgba(6, 182, 212, 0.3);
          box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.4);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: #06b6d4;
          font-size: 20px;
        }

        .stat-value {
          font-size: 20px;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 13px;
          color: #64748b;
        }

        /* Features Section */
        .features-section {
          padding: 60px 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .section-title {
          text-align: center;
          font-size: 32px;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 48px;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 24px;
        }

        .feature-card {
          background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
          border: 1px solid #334155;
          border-radius: 20px;
          padding: 32px;
          transition: all 0.3s ease;
        }

        .feature-card:hover {
          transform: translateY(-4px);
          border-color: rgba(6, 182, 212, 0.3);
          box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.4);
        }

        .feature-icon {
          font-size: 40px;
          margin-bottom: 20px;
        }

        .feature-card h3 {
          font-size: 20px;
          font-weight: 600;
          color: #f1f5f9;
          margin-bottom: 12px;
        }

        .feature-card p {
          font-size: 14px;
          color: #94a3b8;
          line-height: 1.6;
          margin: 0;
        }

        @media (max-width: 768px) {
          .hero-title {
            font-size: 36px;
          }
          
          .hero-subtitle {
            font-size: 16px;
          }
          
          .stats-grid {
            gap: 16px;
          }
          
          .stat-card {
            padding: 20px 24px;
            min-width: 140px;
          }
        }
      `}</style>
    </div>
  );
};

export default Welcome;
